import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { queryClient } from "@/ReactQuery/reactQueryClient";
import {
  currentViewerTimeZone,
  subscribeToViewerTimeZone,
} from "@/utils/viewerTimeZone";

/**
 * Options for fetching files from the API
 */
export interface FileFetchOptions {
  pathPrefix?: string[];
  maxDepth?: number;
  /**
   * Bounds file rows independently of maxDepth. The tree prefetches folder
   * structure one level deeper than it renders (maxDepth), but the file rows
   * at that extra level are never displayed — fetching them shipped every
   * file inside every rendered folder on the root load (#11322). Omitted,
   * files follow maxDepth as before.
   */
  fileMaxDepth?: number;
  driveId?: string;
  excludeBlobKinds?: string[];
}

/**
 * Represents a file entry from the API
 */
export interface FileEntry {
  taskId: string;
  fileId: string;
  fileName: string;
  filePath: string[];
  fileType: string;
  status: string;
  isBlob: boolean;
  isPasswordProtected: boolean;
  /** The password is stored, so the file is unlocked whatever its task is
   *  doing: the row reads plain from the moment a password is accepted (#11856). */
  hasStoredPassword: boolean;
  driveId: string | null;
  uploadedAt: string | null;
  documentDate: string | null;
  // Lineage facts (#11956), present on search results: the lineage this file
  // belongs to, how many OTHER copies and revisions it has, and whether it is
  // the lineage's latest member. Null for a file in no lineage, and absent
  // from listings that do not compute them.
  lineageId?: number | null;
  versionCount?: number | null;
  isLatestVersion?: boolean | null;
}

/**
 * Represents a folder entry from the API
 */
export interface FolderEntry {
  filePath: string[];
  // A bare YYYY-MM-DD calendar day, or null when the entry has no date --
  // a search hit on a path segment resolves no date for the folder (#11055).
  documentDate: string | null;
  driveId: string | null;
}

/**
 * Represents a drive in the file system
 */
export interface Drive {
  _id: string;
  name: string;
  organization_id: string;
  created_at: string | null;
  updated_at: string | null;
  id_text: string | null;
  owned_by_user_id: string | null;
  owned_by_team_id: string | null;
  owned_by_organization: boolean | null;
}

/**
 * Complete response from the files API
 */
export interface FileListing {
  fileList: FileEntry[];
  folders: FolderEntry[];
  drives_listing: {
    drives: Drive[];
    drivesEnabled: boolean;
  };
}

/**
 * Structured metadata payload for Datum objects
 * Discriminated union for type-safe handling of file vs folder metadata
 */
export type RenderableData =
  | {
      type: "folder";
      // Most recent day among the folder contents; null when unresolved.
      documentDate: string | null;
      driveId: string | null;
    }
  | {
      type: "file";
      fileId: string;
      fileName: string;
      fileType: string;
      uploadedAt: string | null;
      documentDate: string | null;
      status: string;
      isBlob: boolean;
      isPasswordProtected: boolean;
      hasStoredPassword: boolean;
      taskId: string;
      driveId: string | null;
    };

/**
 * Convert a FileEntry to RenderableData for use with rendering helpers
 */
export const fileToRenderable = (file: FileEntry): RenderableData => ({
  type: "file",
  fileId: file.fileId,
  fileName: file.fileName,
  fileType: file.fileType,
  uploadedAt: file.uploadedAt,
  documentDate: file.documentDate,
  status: file.status,
  isBlob: file.isBlob,
  isPasswordProtected: file.isPasswordProtected,
  hasStoredPassword: file.hasStoredPassword,
  taskId: file.taskId,
  driveId: file.driveId,
});

/**
 * Query key factory for file tree queries.
 * Uses a hierarchical key structure so invalidateFileTreeCache() can
 * invalidate all file tree queries at once via the ["fileTree"] prefix.
 */
const FILE_TREE_KEYS = {
  all: ["fileTree"] as const,
  files: (
    driveId?: string,
    pathPrefix?: string[],
    maxDepth?: number,
    excludeBlobKinds?: string[],
    // The viewer's zone is part of the key because it is part of the answer:
    // a folder's date is bucketed server-side in whatever zone the request
    // carried (#11055). Leaving it out would serve a listing bucketed in the
    // zone the viewer had before they changed it.
    viewerTimeZone?: string,
    // Part of the key for the same reason: two requests differing only in
    // fileMaxDepth return different fileList contents (#11322).
    fileMaxDepth?: number
  ) =>
    [
      ...FILE_TREE_KEYS.all,
      "files",
      driveId ?? null,
      JSON.stringify(pathPrefix ?? []),
      maxDepth ?? 1,
      JSON.stringify(excludeBlobKinds ?? []),
      viewerTimeZone ?? null,
      fileMaxDepth ?? null,
    ] as const,
};

/**
 * Invalidates all cached file tree queries.
 * Call after operations that change the file system (uploads, moves, deletes)
 * when an immediate refresh is needed before the staleTime expires.
 */
export function invalidateFileTreeCache() {
  queryClient.invalidateQueries({ queryKey: FILE_TREE_KEYS.all });
}

// Pinning a zone has to move the folder rows too, and they are the one date in
// this tree the browser cannot recompute for itself -- the server already
// reduced them to a day (#11055). The file rows re-render from
// `useViewerTimeZone` the moment the preference changes; without this the
// folders above them would keep the old zone's day until the cache went stale.
subscribeToViewerTimeZone(invalidateFileTreeCache);

/**
 * Fetches files and folders from the API.
 * Uses React Query's queryClient.fetchQuery for automatic request
 * deduplication and short-term caching, preventing N+1 API calls when
 * multiple components mount useFiletreeCore simultaneously.
 * @param options - Configuration for the file fetch
 * @returns Promise resolving to file listing data
 */
export async function fetchFiles({
  driveId,
  pathPrefix,
  maxDepth = 1,
  fileMaxDepth,
  excludeBlobKinds,
}: FileFetchOptions = {}): Promise<FileListing> {
  const viewerZone = currentViewerTimeZone();
  return queryClient.fetchQuery({
    queryKey: FILE_TREE_KEYS.files(
      driveId,
      pathPrefix,
      maxDepth,
      excludeBlobKinds,
      viewerZone,
      fileMaxDepth
    ),
    queryFn: async () => {
      const url = new URL(`/api/v2/users/files`, BASE_API_URL_V2);

      if (driveId) {
        url.searchParams.append("drive_id", driveId);
      }

      const normalizedPrefix = pathPrefix || [];
      for (const segment of normalizedPrefix) {
        url.searchParams.append("path_prefix", segment);
      }

      url.searchParams.append("max_depth", String(maxDepth));

      if (fileMaxDepth !== undefined) {
        url.searchParams.append("file_max_depth", String(fileMaxDepth));
      }

      // A folder row's date is an aggregate the server reduces to a day, so it
      // has to be told which zone to read the instants in -- the same one this
      // client renders the file rows in (#11055).
      url.searchParams.append("user_tz", viewerZone);

      if (excludeBlobKinds) {
        for (const kind of excludeBlobKinds) {
          url.searchParams.append("exclude_blob_kinds", kind);
        }
      }

      const response = await fpFetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response?.ok && response?.status !== 401) {
        throw new Error(
          `File API request failed with status ${response?.status}`
        );
      }

      const data = await response.json();
      return data as FileListing;
    },
    staleTime: 30_000,
  });
}

/**
 * Fetches only the available drives
 * @returns Promise resolving to drives data
 */
export async function fetchDrives(): Promise<{
  drives: Drive[];
  drivesEnabled: boolean;
}> {
  const url = new URL(`/api/v2/users/drives`, BASE_API_URL_V2);

  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response?.ok && response?.status !== 401) {
    throw new Error(
      `Drives API request failed with status ${response?.status}`
    );
  }

  const data = await response.json();
  return {
    drives: data.drives || [],
    drivesEnabled: data.drivesEnabled || false,
  };
}

/**
 * Options for searching files
 */
export interface SearchFilesOptions {
  query: string;
  driveId?: string;
  limit?: number;
  signal?: AbortSignal;
  excludeBlobKinds?: string[];
}

/**
 * Search results containing both files and matching folders
 */
export interface SearchResults {
  files: FileEntry[];
  folders: FolderEntry[];
}

/**
 * Searches files and folders by name
 * @param options - Configuration for the search
 * @returns Promise resolving to search results with files and folders
 */
export async function searchFiles(
  options: SearchFilesOptions
): Promise<SearchResults> {
  const url = new URL(`/api/v2/users/files/search`, BASE_API_URL_V2);
  url.searchParams.append("q", options.query);

  if (options.driveId) {
    url.searchParams.append("drive_id", options.driveId);
  }
  if (options.limit) {
    url.searchParams.append("limit", String(options.limit));
  }
  if (options.excludeBlobKinds) {
    for (const kind of options.excludeBlobKinds) {
      url.searchParams.append("exclude_blob_kinds", kind);
    }
  }

  const response = await fpFetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal: options.signal,
  });

  if (!response?.ok) {
    throw new Error(`File search failed: ${response?.status}`);
  }

  const data = await response.json();
  return { files: data.results, folders: data.folders || [] };
}
